import { endReducer } from '@/utils/end-reducer';
import {
  PrintTagBatchFieldsFragment,
  PrintTagTemplateFieldsFragment,
  TagPrintBatchVariantPackFieldsFragment,
} from '@/types';
import { QuantityMapType } from '@/screens/new-order/hooks/use-quantity-reducer';
import { useReducer } from 'react';

export enum TagPrintBatchAction {
  AddItem = 'ADD_ITEM',
  Hydrate = 'HYDRATE',
  RemoveItem = 'REMOVE_ITEM',
  RenameBatch = 'RENAME_BATCH',
  UpdateQuantity = 'UPDATE_TEMPLATE',
  UpdateRetailPrice = 'UPDATE_RETAIL_PRICE',
  UpdateTemplate = 'UPDATE_PRINT_TEMPLATE',
}

export interface TagPrintBatchState {
  initialValue?: TagPrintBatchState;
  name: string;
  quantityMap: QuantityMapType;
  template: PrintTagTemplateFieldsFragment;
  variantPacks: TagPrintBatchVariantPackFieldsFragment[];
}

export type Action =
  | {
      payload: {
        variantPack: TagPrintBatchVariantPackFieldsFragment;
      };
      type: TagPrintBatchAction.AddItem;
    }
  | {
      payload: {
        batch: PrintTagBatchFieldsFragment;
      };
      type: TagPrintBatchAction.Hydrate;
    }
  | {
      payload: {
        variantPackUUID: string;
      };
      type: TagPrintBatchAction.RemoveItem;
    }
  | {
      payload: {
        name: string;
      };
      type: TagPrintBatchAction.RenameBatch;
    }
  | {
      payload: {
        template: PrintTagTemplateFieldsFragment;
      };
      type: TagPrintBatchAction.UpdateTemplate;
    }
  | {
      payload: {
        quantity: number;
        variantPackUUID: string;
      };
      type: TagPrintBatchAction.UpdateQuantity;
    }
  | {
      payload: {
        retailPriceInMicros: number | undefined;
        variantPackUUID: string;
      };
      type: TagPrintBatchAction.UpdateRetailPrice;
    };

const emptyState: TagPrintBatchState = {
  name: '',
  quantityMap: {},
  template: {
    metadata: {},
    uuid: '',
  },
  variantPacks: [],
};

export const createInitialBatchState = (
  batch: PrintTagBatchFieldsFragment,
): TagPrintBatchState => {
  const [quantityMap, variantPacks] = (
    batch.print_tag_batch_variant_packs || []
  ).reduce<[QuantityMapType, TagPrintBatchVariantPackFieldsFragment[]]>(
    (acc, ptbvp) => {
      return [
        {
          ...acc[0],
          [ptbvp.variant_pack.uuid]: ptbvp.quantity,
        },
        [...acc[1], ptbvp.variant_pack!],
      ];
    },
    [{}, []],
  );

  return {
    name: batch.name,
    quantityMap,
    template: batch.print_tag_template,
    variantPacks,
  };
};

const reducer = (state: TagPrintBatchState, action: Action) => {
  switch (action.type) {
    case TagPrintBatchAction.AddItem:
      return {
        ...state,
        quantityMap: {
          ...state.quantityMap,
          [action.payload.variantPack.uuid]: 1,
        },
        variantPacks: [action.payload.variantPack, ...state.variantPacks],
      };
    case TagPrintBatchAction.Hydrate:
      const initialBatchState = createInitialBatchState(action.payload.batch);
      return {
        initialValue: initialBatchState,
        ...initialBatchState,
      };
    case TagPrintBatchAction.RemoveItem:
      const newQuantityMap = { ...state.quantityMap };
      delete newQuantityMap[action.payload.variantPackUUID];

      return {
        ...state,
        quantityMap: newQuantityMap,
        variantPacks: state.variantPacks.filter(
          (vp) => vp.uuid !== action.payload.variantPackUUID,
        ),
      };
    case TagPrintBatchAction.RenameBatch:
      return {
        ...state,
        name: action.payload.name,
      };
    case TagPrintBatchAction.UpdateTemplate:
      return {
        ...state,
        template: action.payload.template,
      };
    case TagPrintBatchAction.UpdateQuantity:
      return {
        ...state,
        quantityMap: {
          ...state.quantityMap,
          [action.payload.variantPackUUID]: action.payload.quantity,
        },
      };
    case TagPrintBatchAction.UpdateRetailPrice:
      return {
        ...state,
        variantPacks: state.variantPacks.map((vp) =>
          vp.uuid === action.payload.variantPackUUID
            ? {
                ...vp,
                restaurant_variant_pack_overrides: [
                  {
                    ...(vp.restaurant_variant_pack_overrides[0] || {}),
                    user_defined_metadata: {
                      list_prices: {
                        retail_price_in_micros:
                          action.payload.retailPriceInMicros,
                      },
                    },
                  },
                ],
              }
            : vp,
        ),
      };
    default:
      return endReducer(state, action);
  }
};

export const useTagPrintBatchReducer = (initialState = emptyState) => {
  return useReducer(reducer, initialState);
};
